import React from 'react';
import PropTypes from 'prop-types';
import { useFormatCurrency } from '@blend-ed/blendxplatform/hooks';

import useRegionalPricing from '../../../hooks/useRegionalPricing';

// The charged/original amounts are in the ORG's currency, formatted by the
// same `useFormatCurrency` the rest of the dashboard uses -- so they carry the
// org's symbol and match every other price on the page.
const format = (amount, formatCurrency) => formatCurrency(amount, {
  maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
});

// The hint is in the LEARNER'S currency, which is NOT the org currency, so
// `useFormatCurrency` (org-scoped) cannot render it. Intl gives the correct
// symbol and grouping per currency code -- "EUR 876.53" -> "€876.53",
// "INR 53031.17" -> "₹53,031.17" -- with a plain-text fallback for an
// unexpected code so the hint degrades to legible rather than throwing.
const formatNative = (amount, code) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
};

/**
 * A price with its regional-discount context.
 *
 * This dashboard reads prices from DDN, which bypasses Django, so unlike the
 * anonymous storefront the discount is applied here on the client. The
 * arithmetic lives in `data/services/regionalPricing` and mirrors blendxapi's
 * `regional_price` exactly -- checkout re-derives the same number server-side,
 * so the two must agree or the learner is quoted one price and charged another.
 *
 * Three parts, each shown only when meaningful, mirroring the anonymous
 * storefront's card: the charged price (always); a struck-through original and
 * a "-N%" badge (only when a discount actually reduced the price -- a 0%
 * home-market tier is a resolved region, not a discount); and an "≈ local"
 * hint (only when an FX rate was available). "Free" for a non-positive price,
 * matching the surrounding cards.
 */
const RegionalPriceLabel = ({ price }) => {
  const formatCurrency = useFormatCurrency();
  const { priceFor } = useRegionalPricing();

  const amount = Number(price);
  if (!Number.isFinite(amount) || amount <= 0) {
    return <>Free</>;
  }

  const {
    price: charged, originalPrice, discountPct, nativeAmount, nativeCurrency,
  } = priceFor(amount);

  const reduced = discountPct > 0 && originalPrice > charged;
  const hasHint = nativeAmount != null && nativeCurrency;
  const chargedLabel = format(charged, formatCurrency);

  return (
    <>
      <span className="regional-price__amount">{chargedLabel}</span>

      {reduced && (
        <>
          <span className="regional-price__original">
            {format(originalPrice, formatCurrency)}
          </span>
          <span className="regional-price__discount">{`-${discountPct}%`}</span>
        </>
      )}

      {hasHint && (
        <span
          className="regional-price__hint"
          // Display-only and approximate: the learner is billed `chargedLabel`
          // in the org currency; the gateway converts at its own rate.
          title={`Approximate. You will be charged ${chargedLabel}.`}
        >
          {`≈ ${formatNative(nativeAmount, nativeCurrency)}`}
        </span>
      )}
    </>
  );
};

RegionalPriceLabel.propTypes = {
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

RegionalPriceLabel.defaultProps = {
  price: 0,
};

export default RegionalPriceLabel;
